// Inline Chart.js plugins for the Market Data charting app. Each plugin reads
// its config from chart.options.plugins.<name> and stays inert when absent, so
// they are safe to pass on every chart. The matplotlib renderer draws the same
// artefacts server-side (chart_render.py) — keep the visuals aligned.

import { US_RECESSION_BANDS } from './chartConfigSchema';

const NAVY = '#072040';
const GRID_GREY = '#E7E6E6';

const DASH = { solid: [], dashed: [6, 4], dotted: [2, 3] };

// Export scaling: buildExportCanvas sets `fontScale` on each plugin's config
// while it renders the 1920x1080 export, so plugin-drawn text/strokes grow by
// the same factor as the Chart.js fonts. Absent (normal display) → 1.
const scaleOf = (cfg) => cfg?.fontScale || 1;
const px = (size, cfg) => `${Math.round(size * scaleOf(cfg))}px "Segoe UI", Arial, sans-serif`;

// Horizontal line at y = 0 on the left axis.
export const zeroLinePlugin = {
  id: 'zeroLine',
  afterDatasetsDraw(chart) {
    const cfg = chart.options.plugins?.zeroLine;
    if (!cfg?.enabled) return;
    const yScale = chart.scales.y;
    if (!yScale) return;
    const y = yScale.getPixelForValue(0);
    if (y < chart.chartArea.top || y > chart.chartArea.bottom) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = NAVY;
    ctx.lineWidth = scaleOf(cfg);
    ctx.beginPath();
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.restore();
  },
};

// Formatted label at each dataset's last non-null point, in the series colour.
export const lastValueLabelsPlugin = {
  id: 'lastValueLabels',
  afterDatasetsDraw(chart) {
    const cfg = chart.options.plugins?.lastValueLabels;
    if (!cfg?.enabled) return;
    const { ctx } = chart;
    const k = scaleOf(cfg);
    ctx.save();
    ctx.font = px(11, cfg);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    chart.data.datasets.forEach((ds, di) => {
      if (ds.skipLastValueLabel) return;
      const meta = chart.getDatasetMeta(di);
      if (!meta || meta.hidden) return;
      for (let i = ds.data.length - 1; i >= 0; i--) {
        const v = ds.data[i];
        if (v == null) continue;
        const el = meta.data[i];
        if (!el) break;
        const fmt = cfg.format?.[ds.yAxisID || 'y'];
        ctx.fillStyle = ds.borderColor || NAVY;
        ctx.fillText(fmt ? fmt(v) : v.toLocaleString('en-AU', { maximumFractionDigits: 1 }),
                     el.x + 4 * k, el.y);
        break;
      }
    });
    ctx.restore();
  },
};

// Shaded date bands (incl. the NBER US recession preset), vertical event
// lines and horizontal reference lines. Bands/vlines locate their dates on
// the chart's label array (the visible date window).
export const annotationsPlugin = {
  id: 'chartAnnotations',
  beforeDatasetsDraw(chart) {
    const cfg = chart.options.plugins?.chartAnnotations;
    if (!cfg) return;
    const labels = chart.data.labels || [];
    if (!labels.length) return;
    const xScale = chart.scales.x;
    const { ctx, chartArea } = chart;

    let bands = cfg.bands || [];
    if (cfg.preset_bands === 'us_recessions') {
      bands = bands.concat(US_RECESSION_BANDS.map(([start, end]) => ({ start, end })));
    }
    ctx.save();
    bands.forEach(band => {
      let i0 = -1, i1 = -1;
      for (let i = 0; i < labels.length; i++) {
        if (labels[i] >= band.start && labels[i] <= band.end) {
          if (i0 < 0) i0 = i;
          i1 = i;
        }
      }
      if (i0 < 0) return;
      const x0 = xScale.getPixelForValue(Math.max(0, i0 - 0.5));
      const x1 = xScale.getPixelForValue(Math.min(labels.length - 1, i1 + 0.5));
      ctx.fillStyle = 'rgba(231, 230, 230, 0.45)';
      ctx.fillRect(x0, chartArea.top, x1 - x0, chartArea.bottom - chartArea.top);
      if (band.label) {
        ctx.fillStyle = NAVY;
        ctx.font = px(10, cfg);
        ctx.textAlign = 'center';
        ctx.fillText(band.label, (x0 + x1) / 2, chartArea.top + 12 * scaleOf(cfg));
      }
    });
    ctx.restore();
  },
  afterDatasetsDraw(chart) {
    const cfg = chart.options.plugins?.chartAnnotations;
    if (!cfg) return;
    const labels = chart.data.labels || [];
    const xScale = chart.scales.x;
    const { ctx, chartArea } = chart;
    ctx.save();

    const k = scaleOf(cfg);
    (cfg.vlines || []).forEach(vline => {
      if (!labels.length) return;
      if (vline.date < labels[0] || vline.date > labels[labels.length - 1]) return;
      const idx = labels.findIndex(d => d >= vline.date);
      if (idx < 0) return;
      const x = xScale.getPixelForValue(idx);
      ctx.strokeStyle = 'rgba(7, 32, 64, 0.7)';
      ctx.lineWidth = k;
      ctx.setLineDash(DASH.dashed);
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      if (vline.label) {
        ctx.fillStyle = NAVY;
        ctx.font = px(10, cfg);
        ctx.textAlign = 'right';
        ctx.save();
        ctx.translate(x - 3 * k, chartArea.top + 3 * k);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = 'right';
        ctx.fillText(vline.label, 0, 0);
        ctx.restore();
      }
    });

    (cfg.hlines || []).forEach(hline => {
      const scale = chart.scales[hline.axis === 'y1' && chart.scales.y1 ? 'y1' : 'y'];
      if (!scale) return;
      const y = scale.getPixelForValue(hline.value);
      if (y < chartArea.top || y > chartArea.bottom) return;
      ctx.strokeStyle = 'rgba(7, 32, 64, 0.7)';
      ctx.lineWidth = k;
      ctx.setLineDash(DASH[hline.style || 'dashed'] || DASH.dashed);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      if (hline.label) {
        ctx.fillStyle = NAVY;
        ctx.font = px(10, cfg);
        ctx.textAlign = 'right';
        ctx.fillText(hline.label, chartArea.right - 4 * k, y - 5 * k);
      }
    });
    ctx.restore();
  },
};

// Mean ± N·SD bands over the visible window for flagged datasets. Values are
// precomputed in the component (same slice as the server renderer).
export const sdBandsPlugin = {
  id: 'sdBands',
  afterDatasetsDraw(chart) {
    const cfg = chart.options.plugins?.sdBands;
    if (!cfg?.bands?.length) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    cfg.bands.forEach(({ axis, mean, sd, nSd, colour }) => {
      const scale = chart.scales[axis === 'y1' && chart.scales.y1 ? 'y1' : 'y'];
      if (!scale) return;
      const drawLine = (value, dash, alpha) => {
        const y = scale.getPixelForValue(value);
        if (y < chartArea.top || y > chartArea.bottom) return;
        ctx.strokeStyle = colour;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = scaleOf(cfg);
        ctx.setLineDash(dash);
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
      };
      drawLine(mean, DASH.dashed, 0.6);
      drawLine(mean + nSd * sd, DASH.dotted, 0.4);
      drawLine(mean - nSd * sd, DASH.dotted, 0.4);
    });
    ctx.restore();
  },
};

export const GRID_COLOURS = { NAVY, GRID_GREY };
